import { queryOptions, useQuery } from "@tanstack/react-query";

import { workbenchActionsApi } from "../../../api/client";
import { actionsKeys } from "./actionsKeys";

export interface WorkbenchAction {
  id: string;
  label: string;
}

/**
 * Shared queryOptions for the actions a role may run on a deal. Backed by
 * `WorkbenchActionsApi.getActions`; rows keep server order and the default-action
 * flags are dropped. Entries missing an id or label are discarded.
 * @param {string} approvalId - The deal / approval identifier.
 * @param {string} roleName - The analyst's role name.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function workbenchActionsQuery(approvalId: string, roleName: string) {
  return queryOptions({
    queryKey: actionsKeys.list({ approvalId, roleName }),
    queryFn: async ({ signal }): Promise<WorkbenchAction[]> => {
      const response = await workbenchActionsApi.getActions(approvalId, roleName, { signal });
      const actions: WorkbenchAction[] = [];
      for (const { id, label } of response.data.actions ?? []) {
        if (id && label) {
          actions.push({ id, label });
        }
      }
      return actions;
    },
    staleTime: 30_000,
    enabled: !!approvalId && !!roleName,
  });
}

/**
 * Reads the actions available to the role on a deal. Disabled until both the
 * approval id and role name are present.
 * @param {string} approvalId - The deal / approval identifier.
 * @param {string} roleName - The analyst's role name.
 * @returns {ReturnType<typeof useQuery<WorkbenchAction[]>>} The TanStack Query result.
 */
export function useWorkbenchActions(approvalId: string, roleName: string) {
  return useQuery(workbenchActionsQuery(approvalId, roleName));
}
